import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [brand, setBrand] = useState({ site_name: "Cheltenham Township", site_tagline: "Tax Filing Portal" });

  useEffect(() => {
    api
      .get("/settings/access")
      .then(({ data }) => setSignupEnabled(!!data.signup_enabled))
      .catch(() => {});
    api
      .get("/settings/brand")
      .then(({ data }) => setBrand(data))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Signed in");
      navigate(u.role === "admin" || u.role === "staff" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="civic-hero hidden lg:flex flex-col justify-between p-12 text-white">
        <Link to="/" className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center">
            <Landmark className="w-6 h-6" />
          </span>
          <div>
            <p className="font-heading font-semibold" data-testid="login-brand-name">{brand.site_name}</p>
            <p className="text-sm text-slate-200">{brand.site_tagline}</p>
          </div>
        </Link>
        <div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight leading-tight">
            Mercantile &amp; Business Privilege Tax Return
          </h2>
          <p className="mt-4 text-slate-200 max-w-md leading-relaxed">
            Secure access for {brand.site_name} merchants and the Finance &amp; Tax Office.
          </p>
        </div>
        <p className="text-sm text-slate-300">Montgomery County, Pennsylvania</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
            Sign in
          </h1>
          <p className="mt-2 text-slate-500">Access your filings dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@business.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          </form>

          {signupEnabled && (
            <p className="mt-6 text-sm text-slate-500">
              New merchant?{" "}
              <Link to="/register" className="text-[#1E3A8A] font-medium hover:underline" data-testid="to-register-link">
                Create an account
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
